import {
  type DisplayableLoadingStep,
  LOADING_STEP_LABELS,
  LOADING_STEP_ORDER,
  type LoadingStep,
} from 'features/kestrelViewer/types/kestrelLoading'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import AnimatedBirds from './animations/AnimatedBirds'

const STEP_ORDER: ReadonlyArray<DisplayableLoadingStep> =
  LOADING_STEP_ORDER.filter((s): s is DisplayableLoadingStep => s !== 'idle')

const RECTANGLE_COUNT = STEP_ORDER.length

const KestrelContainer = styled.div`
  position: relative;
  width: 360px;
  height: 240px;
`

const LoadingBarContainer = styled.div`
  position: absolute;
  bottom: -12px;
  left: 0;
  width: 100%;
  height: 6px;
  display: flex;
  gap: 2px;
  align-items: center;
`

const LoadingRectangle = styled.div<{ $isFilled: boolean }>`
  flex: 1;
  height: 100%;
  background: ${(props) =>
    props.$isFilled ? '#4a90e2' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 1px;
  transition: background 0.2s ease-out;
`

const Container = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  z-index: 10000;
  pointer-events: none;
`

const isDisplayableStep = (
  step: LoadingStep | undefined
): step is DisplayableLoadingStep => step !== undefined && step !== 'idle'

const KestrelLoadingWidget: React.FC<{
  showLoading: boolean
  hasError?: boolean
  currentStep?: LoadingStep
  showStepLabel?: boolean
}> = ({
  showLoading,
  hasError = false,
  currentStep,
  showStepLabel = false,
}) => {
  const [shouldRender, setShouldRender] = useState(showLoading)
  const [maxStepIndex, setMaxStepIndex] = useState<number>(-1)

  useEffect(() => {
    if (hasError) {
      setShouldRender(false)
      return
    }

    if (showLoading && !shouldRender) {
      setShouldRender(true)
    }

    if (!showLoading && shouldRender) {
      setShouldRender(false)
    }
  }, [showLoading, shouldRender, hasError])

  useEffect(() => {
    if (!showLoading) {
      setMaxStepIndex(-1)
      return
    }

    if (!currentStep) {
      return
    }

    if (!isDisplayableStep(currentStep)) {
      return
    }

    const currentIndex = STEP_ORDER.indexOf(currentStep)
    if (currentIndex === -1) {
      return
    }

    setMaxStepIndex((prev) => Math.max(prev, currentIndex))
  }, [showLoading, currentStep])

  if (!shouldRender) return null

  const currentStepIndex = isDisplayableStep(currentStep)
    ? STEP_ORDER.indexOf(currentStep)
    : -1
  const effectiveIndex = Math.max(currentStepIndex, maxStepIndex)
  const filledRectangles = effectiveIndex >= 0 ? effectiveIndex + 1 : 0

  const displayText =
    showStepLabel &&
    isDisplayableStep(currentStep) &&
    LOADING_STEP_LABELS[currentStep]
      ? `${LOADING_STEP_LABELS[currentStep]}...`
      : ''

  return (
    <Container>
      <KestrelContainer>
        <AnimatedBirds count={40} color='#ffffff' size={2.4} />
        <LoadingBarContainer>
          {Array.from({ length: RECTANGLE_COUNT }).map((_, index) => (
            <LoadingRectangle
              key={index}
              $isFilled={index < filledRectangles}
            />
          ))}
        </LoadingBarContainer>
      </KestrelContainer>
      {displayText && <p>{displayText}</p>}
    </Container>
  )
}

export default KestrelLoadingWidget
